import React from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronLeft, ChevronRight, Circle } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { formatDateInTz } from '../../../utils/timezone'

interface Occurrence {
  occurrenceNumber: number
  scheduledAt: string
}

interface Props {
  pastOccurrences: Occurrence[]
  currentNumber: number
  currentScheduledAt: string
  seriesClosed?: boolean
  viewingNumber: number | null
  onNavigate: (occurrenceNumber: number | null) => void
  timezone?: string | null
}

const OccurrenceNavigator: React.FC<Props> = ({
  pastOccurrences,
  currentNumber,
  currentScheduledAt,
  seriesClosed,
  viewingNumber,
  onNavigate,
  timezone,
}) => {
  const { t, i18n } = useTranslation()
  const fmt = (s: string) => formatDateInTz(s, timezone, 'PP', i18n.language)
  const sortedPast = [...pastOccurrences].sort((a, b) => a.occurrenceNumber - b.occurrenceNumber)

  const isViewingCurrent = !seriesClosed && (viewingNumber === null || viewingNumber === currentNumber)
  const viewing =
    !isViewingCurrent
      ? sortedPast.find((o) => o.occurrenceNumber === viewingNumber) ?? sortedPast[sortedPast.length - 1]
      : { occurrenceNumber: currentNumber, scheduledAt: currentScheduledAt }

  if (!viewing) return null

  const currentIndex = isViewingCurrent
    ? sortedPast.length
    : sortedPast.findIndex((o) => o.occurrenceNumber === viewing.occurrenceNumber)

  const prev = currentIndex > 0 ? sortedPast[currentIndex - 1] : null
  const next = isViewingCurrent
    ? null
    : seriesClosed
      ? sortedPast[currentIndex + 1] || null
      : sortedPast[currentIndex + 1] || 'current'

  const goTo = (target: Occurrence | 'current' | null) => {
    if (target === 'current') onNavigate(null)
    else if (target) onNavigate(target.occurrenceNumber)
  }

  if (sortedPast.length === 0) return null

  return (
    <div className="flex items-center gap-2 text-sm">
      <button
        type="button"
        onClick={() => prev && goTo(prev)}
        disabled={!prev}
        className={cn(
          'inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs transition-colors',
          prev ? 'hover:bg-muted text-foreground' : 'opacity-40 cursor-not-allowed text-muted-foreground'
        )}
        title={prev ? `${t('Meeting_occurrence_prev')} (#${prev.occurrenceNumber} · ${fmt(prev.scheduledAt)})` : ''}
      >
        <ChevronLeft className="h-4 w-4" />
        {prev ? `#${prev.occurrenceNumber} · ${fmt(prev.scheduledAt)}` : t('Meeting_occurrence_first')}
      </button>

      <TooltipProvider delayDuration={150}>
        <div className="flex items-center gap-1.5 text-xs">
          {sortedPast.map((o) => {
            const isViewed = !isViewingCurrent && viewingNumber === o.occurrenceNumber
            return (
              <Tooltip key={o.occurrenceNumber}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => goTo(o)}
                    className={cn(
                      'h-2.5 w-2.5 rounded-full transition-colors',
                      isViewed ? 'bg-amber-500 ring-2 ring-amber-200' : 'bg-slate-400 hover:bg-slate-600'
                    )}
                  />
                </TooltipTrigger>
                <TooltipContent side="top" className="text-xs">
                  <span className="font-medium">#{o.occurrenceNumber}</span>
                  <span className="text-muted-foreground"> · {fmt(o.scheduledAt)}</span>
                </TooltipContent>
              </Tooltip>
            )
          })}
          {!seriesClosed && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => goTo('current')}
                  className={cn(
                    'h-3 w-3 rounded-full transition-colors',
                    isViewingCurrent ? 'bg-primary ring-2 ring-primary/20' : 'bg-primary/40 hover:bg-primary'
                  )}
                />
              </TooltipTrigger>
              <TooltipContent side="top" className="text-xs">
                <span className="font-medium">#{currentNumber}</span>
                <span className="text-muted-foreground"> · {fmt(currentScheduledAt)} · {t('Meeting_occurrence_current_short')}</span>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      </TooltipProvider>

      <button
        type="button"
        onClick={() => next && goTo(next)}
        disabled={!next}
        className={cn(
          'inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs transition-colors',
          next ? 'hover:bg-muted text-foreground' : 'opacity-40 cursor-not-allowed text-muted-foreground'
        )}
        title={
          next === 'current'
            ? `${t('Meeting_occurrence_current')} (#${currentNumber})`
            : next
              ? `${t('Meeting_occurrence_next')} (#${next.occurrenceNumber} · ${fmt(next.scheduledAt)})`
              : ''
        }
      >
        {next === 'current'
          ? `#${currentNumber} · ${t('Meeting_occurrence_current_short')}`
          : next
            ? `#${next.occurrenceNumber} · ${fmt(next.scheduledAt)}`
            : t('Meeting_occurrence_last')}
        <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  )
}

export default OccurrenceNavigator
